import { Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, delay, filter, first, map, shareReplay, switchMap } from 'rxjs/operators';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { ReCaptchaV3Service } from 'ng-recaptcha';

import { IAppConfig } from '@interfaces/app-config.interface';
import { AppConfigService } from '@services/app-config.service';
import { AuthTokenService } from '@services/auth-token.service';
import { UserService } from '@services/user.service';

const noauthPlaceholder = 'LDAP';
const warningMessages = {
  validations: 'Please enter valid credentials',
  errorAuth: 'Incorrect login or password'
};

@Component({
  selector: 'app-auth-page',
  templateUrl: './auth-page.component.html',
  styleUrls: ['./auth-page.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AuthPageComponent implements OnInit, OnDestroy {
  public loginWithGoogle: boolean;
  public loginButtonDisabled: boolean;
  public noauthButtonDisabled: boolean;
  public credentialsError: boolean;
  public inputType: string;
  public isPasswordVisible: boolean;
  public notCorrectCredentials: boolean;
  public wrongGoogleAccount: boolean;
  public capsLock: boolean;
  public messages: Object;

  public loginControl: FormControl;
  public passwordControl: FormControl;
  public noauthControl: FormControl;

  public loginForm: FormGroup;

  public isLoggingIn = false;

  public showHint: boolean;
  public tooltipStyles = {
    backgroundColor: 'rgba(74,74,74, 0.95)',
    fontSize: '14px',
    color: '#ffffff',
    maxWidth: '300px',
    boxShadow: 'none',
    border: 'none',
    zIndex: 1000,
    lineHeight: '20px',
    padding: '10px 12px'
  };

  private _subs: Subscription[] = [];
  private _redirectTo: string;
  private _isProduction = true;

  private _loadScript$: Observable<null>;

  @ViewChild('googleButtonWrapper') googleButtonWrapper: ElementRef;

  constructor(
    private userService: UserService,
    private appConfigService: AppConfigService,
    private authTokenService: AuthTokenService,
    private activatedRoute: ActivatedRoute,
    private zone: NgZone,
    private changeDetectorRef: ChangeDetectorRef,
    private fb: FormBuilder,
    private recaptchaV3Service: ReCaptchaV3Service
  ) {}

  @HostListener('document:keyup', ['$event']) clickHandler(event: any) {
    this.capsLock = event.getModifierState && event.getModifierState('CapsLock');
  }

  @HostListener('document:keydown', ['$event']) clickHandler1(event: any) {
    this.capsLock = event.getModifierState && event.getModifierState('CapsLock');
  }

  ngOnInit(): void {
    this._init();
    this._subscribe();
    this.zone.onError.subscribe((error) => {
      if (error) {
        this.isLoggingIn = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  public get isProduction(): boolean {
    return this._isProduction;
  }

  public get isNoAuthButtonDisabled(): boolean {
    const trimValue = this.noauthControl.value.trim();
    return !trimValue || trimValue === noauthPlaceholder;
  }

  public loginFocusHandler(): void {
    this.showHint = true;
  }

  public loginBlurHandler(): void {
    this.showHint = false;
  }

  public toggleInputType(): void {
    this.inputType = this.inputType === 'password' ? 'text' : 'password';
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  public toggleAuthMethod(): void {
    this.loginWithGoogle = !this.loginWithGoogle;
    this.wrongGoogleAccount = false;

    if (this.loginWithGoogle) {
      this.showGoogleButton();
      this.loginControl.reset();
      this.passwordControl.reset();
    }
  }

  public showGoogleButton(): void {
    this._loadScript$.pipe(delay(100)).subscribe(() => {
      // @ts-ignore
      window.google.accounts.id.prompt();

      // @ts-ignore
      window.google.accounts.id.renderButton(this.googleButtonWrapper.nativeElement, {
        type: 'standard',
        size: 'large',
        width: '260px'
      });
    });
  }

  public googleAuth(credential: string): void {
    this.isLoggingIn = true;

    this._subs.push(
      this.reCaptchaRequest()
        .pipe(
          first(),
          switchMap((captchaToken) => this.authTokenService.getNewToken(credential, captchaToken))
        )
        .subscribe(
          () => {
            this.isLoggingIn = false;
            window.location.href = this._redirectTo;
          },
          (err) => {
            console.error(err);
            this.wrongGoogleAccount = true;
            this.isLoggingIn = false;
          }
        )
    );
  }

  public signIn() {
    const password = this.passwordControl.value;
    const login = this.loginControl.value;

    this.isLoggingIn = true;

    if (login.length > 0 && password.length > 0) {
      this._subs.push(
        this.reCaptchaRequest()
          .pipe(switchMap((captchaToken) => this.userService.loginLdap(login, password, captchaToken)))
          .subscribe(
            () => {
              window.location.href = this._redirectTo;
            },
            (error: any) => {
              this.notCorrectCredentials = true;
              this.isLoggingIn = false;
              return throwError(error);
            }
          )
      );

      return true;
    }

    return false;
  }

  public signInNoauth() {
    const ldap = this.noauthControl.value;

    this.isLoggingIn = true;

    if (ldap.length > 0 && ldap !== noauthPlaceholder) {
      this.noauthButtonDisabled = true;
      this.userService.loginNoAuthMode(ldap).subscribe(() => {
        window.location.href = this._redirectTo;
      });
    }

    return false;
  }

  public noauthFocusHandler(): void {
    if (this.noauthControl.value === noauthPlaceholder) {
      this.noauthControl.setValue('');
    }
  }

  public noauthBlurHandler(): void {
    if (this.noauthControl.value === '') {
      this.noauthControl.setValue(noauthPlaceholder);
    }
  }

  private _init() {
    this.loginButtonDisabled = true;
    this.noauthButtonDisabled = true;
    this.wrongGoogleAccount = false;
    this.credentialsError = false;
    this.inputType = 'password';
    this.isPasswordVisible = false;
    this.notCorrectCredentials = false;
    this.capsLock = false;
    this.messages = warningMessages;
    this._loadScript$ = new Observable((subscriber) => {
      const signInJS = document.createElement('script');

      signInJS.async = true;
      signInJS.defer = true;
      signInJS.src = 'https://accounts.google.com/gsi/client';
      signInJS.onload = () => {
        subscriber.next(null);
        subscriber.complete();
      };
      signInJS.onerror = () => {
        subscriber.error(null);
        subscriber.complete();
      };

      document.head.appendChild(signInJS);
    }).pipe(shareReplay<null>(1));

    this._buildForm();
    this._toggleCorrectCredentialVariable();

    this.appConfigService.config$
      .pipe(
        filter((x) => !!x),
        switchMap((config: IAppConfig) =>
          this.activatedRoute.queryParams.pipe(
            map((params) => ({
              redirectTo: this._extractRedirectParam(params),
              config
            }))
          )
        )
      )
      .subscribe(({ redirectTo, config }) => {
        this.loginWithGoogle = config.production;
        this._isProduction = config.production;
        this._redirectTo = redirectTo ? redirectTo : config.defaultRedirectUrl;
      });

    this._loadScript$.subscribe(() => {
      // @ts-ignore
      const { google } = window;

      google.accounts.id.initialize({
        client_id: this.appConfigService.config.googleClientID,
        scope: 'email',
        cancel_on_tap_outside: false,
        callback: (response) => {
          this.googleAuth(response?.credential);
        }
      });

      if (this.loginWithGoogle) {
        this.showGoogleButton();
      }
    });
  }

  private _subscribe(): void {
    const noauthSub = this.noauthControl.valueChanges.subscribe((value: string) => {
      this.noauthButtonDisabled = !value || value === noauthPlaceholder;
    });

    this._subs.push(noauthSub);
  }

  private _extractRedirectParam(params: Params): string | undefined {
    const redirectTo = params['redirectTo'];
    try {
      const { hostname } = new URL(redirectTo);
      const regexp = /\.griddynamics\.(com|net)$/;

      if (!regexp.test(hostname)) {
        return;
      }

      return redirectTo;
    } catch (e) {
      return;
    }
  }

  private _buildForm(): void {
    this.loginControl = new FormControl('', [Validators.required, Validators.pattern('[a-zA-Z0-9]*')]);
    this.passwordControl = new FormControl('', [Validators.required]);
    this.noauthControl = new FormControl(noauthPlaceholder);

    this.loginForm = this.fb.group({
      login: this.loginControl,
      password: this.passwordControl
    });
  }

  private _toggleCorrectCredentialVariable(): void {
    this.loginForm.valueChanges.subscribe(() => {
      this.notCorrectCredentials = false;
      this.wrongGoogleAccount = false;
    });
  }

  private reCaptchaRequest(): Observable<string> {
    return this.recaptchaV3Service.execute('sign_in').pipe(
      first(),
      catchError(() => of(''))
    );
  }

  ngOnDestroy(): void {
    this._subs.forEach((sub) => sub.unsubscribe());
  }
}
